/** One document the screen intends to pay, in the document's own currency (#5662). */
export interface PoReceiptCoverageRequest {
  documentReference: string;
  amount: number;
  /** A row on screen that is not selected: checked as if added to the batch, but counted toward no other
   *  document. Selected rows are checked together — two invoices on one PO add up. */
  candidate?: boolean;
}

/** One purchase order a document draws from, checked against what actually arrived (#5662). All pre-tax. */
export interface PoReceiptCoverageLine {
  purchaseOrderReference: string;
  purchaseOrderNumber: string | null;
  /** False when the PO could not be checked (not found, or mixed currencies with no functional receipt
   *  value) — every figure is then 0 and means nothing. */
  checked: boolean;
  /** True when a document on the PO is in another currency, so the figures are functional. */
  comparedInFunctional: boolean;
  currency: number | null;
  netReceived: number;
  paidToDate: number;
  payingNow: number;
  /** What the other documents in the same request or payment add to this PO — the batch is checked together. */
  payingWithOthers: number;
  /** How far paid + paying (this document and the others) exceeds received; 0 when it does not. */
  shortfall: number;
  /** The AP documents behind paidToDate and payingWithOthers, in the same basis. */
  documents: PoReceiptCoverageDocument[];
}

/** One AP document's share of a PO in a coverage check, pre-tax, in its line's basis. */
export interface PoReceiptCoverageDocument {
  documentReference: string;
  documentNumber: string | null;
  fiscalNumber: string | null;
  /** The document the check is about. */
  isThisDocument: boolean;
  /** Part of the same request or payment being checked. */
  inThisBatch: boolean;
  paidToDate: number;
  payingNow: number;
}

/** Whether paying a document would take any of its POs past what was received (#5662). */
export interface PoReceiptCoverage {
  documentReference: string;
  documentNumber: string | null;
  exceedsReceived: boolean;
  purchaseOrders: PoReceiptCoverageLine[];
}

/** GlobalEntityDefinition codes of the receiving documents the ledger reads (#5661). */
export enum PoReceiptSourceType {
  Shipment = 7,
  GoodsReceipt = 16,
  ProviderReturn = 18,
}

/** One receiving document's contribution to a PO: a GRN or shipment (effect +1) or a provider return (−1). */
export interface PoReceiptSource {
  sourceReference: string;
  sourceEntityDefinitionCode: number;
  sourceNumber: string | null;
  effect: number;
  receivedDate: string;
  /** Pre-tax, PO currency, unsigned — `effect` carries the direction. */
  amount: number;
  functionalAmount: number | null;
}

/** What was received against one PO, per item, pre-tax (#5661). */
export interface PoReceiptSummaryItem {
  itemCode: string;
  purchaseOrderItemReference: string | null;
  unitPrice: number;
  orderedQuantity: number;
  orderedAmount: number;
  receivedQuantity: number;
  receivedAmount: number;
  returnedQuantity: number;
  returnedAmount: number;
  netReceivedQuantity: number;
  netReceivedAmount: number;
  netReceivedFunctionalAmount: number | null;
}

export interface PoReceiptSummary {
  purchaseOrderReference: string;
  purchaseOrderNumber: string | null;
  currency: number | null;
  functionalCurrency: number | null;
  receivedAmount: number;
  returnedAmount: number;
  netReceivedAmount: number;
  netReceivedFunctionalAmount: number | null;
  items: PoReceiptSummaryItem[];
  receipts: PoReceiptSource[];
}
